import { Colors, Container, Heading, Text } from '@amelio/ui-library';

import type { ContainerProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { MEETINGS_TITLE } from '@/components/MeetingPlan/meetingPlanStyles';

interface PlanSectionHeadingProps {
    title: string;
    /** One line under the title, saying what the section is for. */
    description?: string;
}

/** The title every section of the meeting plan opens with, whichever card draws the section. */
export const PlanSectionHeading = ({ title, description }: PlanSectionHeadingProps) => (
    <div className={'flex flex-col gap-2'}>
        <Heading
            level={3}
            color={Colors.NAVY}
            className={MEETINGS_TITLE}
        >
            {title}
        </Heading>

        {description && (
            <Text
                size={'small'}
                color={Colors.BASE_500}
            >
                {description}
            </Text>
        )}
    </div>
);

interface PlanCardProps {
    dataTestId: string;
    className?: string;
    /** `'none'` for a block that draws its own bands edge to edge, such as the aspirations banner. */
    padding?: ContainerProps['p'];
    /** `'none'` for a block drawing its own outline, such as the end-of-meeting panel. */
    border?: ContainerProps['border'];
    children: ReactNode;
}

/** The glass card every block of the meeting plan is drawn in, with no second card nested inside. */
export const PlanCard = ({
    dataTestId,
    className,
    padding = 'xl',
    border = 'subtle',
    children,
}: PlanCardProps) => (
    <Container
        p={padding}
        border={border}
        borderRadius={'2xl'}
        background={'translucent-strong'}
        glass={'lg'}
        className={className}
        dataTestId={dataTestId}
    >
        {children}
    </Container>
);

interface PlanSectionProps extends PlanSectionHeadingProps {
    dataTestId: string;
    children: ReactNode;
}

/** A section of the meeting plan: its card, its title, then its content. */
export const PlanSection = ({ title, description, dataTestId, children }: PlanSectionProps) => (
    <PlanCard dataTestId={dataTestId}>
        <PlanSectionHeading
            title={title}
            description={description}
        />

        <div className={'mt-6'}>{children}</div>
    </PlanCard>
);
